// Tiny vanilla-JS document head manager. No deps. Mounts <title> + the
// canonical OG / Twitter Card meta tags into <head> for the duration of
// the component lifecycle and restores them on unmount.
//
// Why this exists: our React SPA serves the same /public/index.html for
// every route, so link previewers fetching /stories/{slug} only saw the
// generic site title. Modern link previewers (iMessage on iOS 16+,
// Telegram, Slack, WhatsApp, etc.) execute the page before snapshotting
// metadata, so updating the DOM head from React is enough to give them
// the right title, description and image. Non-JS crawlers (older Twitter
// bot, Facebook scraper) still get the generic tags — for those, the
// in-app Share button always shares the OG-rich /api/share/stories/{slug}
// URL which is server-rendered with rich tags.

import { useEffect } from 'react';

const META_KEYS = [
  { sel: 'meta[property="og:title"]',       attr: 'property', name: 'og:title' },
  { sel: 'meta[property="og:description"]', attr: 'property', name: 'og:description' },
  { sel: 'meta[property="og:image"]',       attr: 'property', name: 'og:image' },
  { sel: 'meta[property="og:url"]',         attr: 'property', name: 'og:url' },
  { sel: 'meta[property="og:type"]',        attr: 'property', name: 'og:type' },
  { sel: 'meta[property="og:site_name"]',   attr: 'property', name: 'og:site_name' },
  { sel: 'meta[name="twitter:card"]',        attr: 'name',     name: 'twitter:card' },
  { sel: 'meta[name="twitter:title"]',       attr: 'name',     name: 'twitter:title' },
  { sel: 'meta[name="twitter:description"]', attr: 'name',     name: 'twitter:description' },
  { sel: 'meta[name="twitter:image"]',       attr: 'name',     name: 'twitter:image' },
  { sel: 'meta[name="description"]',         attr: 'name',     name: 'description' },
];

const upsertMeta = (sel, attr, name, content) => {
  if (typeof document === 'undefined' || !content) return null;
  let el = document.querySelector(sel);
  const created = !el;
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  const prev = el.getAttribute('content');
  el.setAttribute('content', String(content));
  return { el, prev, created };
};

const upsertLink = (rel, href) => {
  if (typeof document === 'undefined' || !href) return null;
  let el = document.querySelector(`link[rel="${rel}"]`);
  const created = !el;
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  const prev = el.getAttribute('href');
  el.setAttribute('href', href);
  return { el, prev, created };
};

/**
 * Set page <title> and OG/Twitter meta tags.
 *
 * @param {{
 *   title: string,
 *   description?: string,
 *   image?: string,
 *   url?: string,
 *   siteName?: string,
 *   type?: string,
 * }} opts
 */
export default function useDocumentHead({
  title,
  description,
  image,
  url,
  siteName = 'Dreams League',
  type = 'article',
}) {
  useEffect(() => {
    if (typeof document === 'undefined' || !title) return undefined;

    const prevTitle = document.title;
    document.title = `${title} — ${siteName}`;

    const restorers = [];

    const set = (sel, attr, name, content) => {
      const r = upsertMeta(sel, attr, name, content);
      if (r) restorers.push(() => {
        if (r.created) r.el.remove();
        else if (r.prev !== null) r.el.setAttribute('content', r.prev);
        else r.el.removeAttribute('content');
      });
    };

    set('meta[name="description"]',         'name',     'description',         description);
    set('meta[property="og:type"]',         'property', 'og:type',             type);
    set('meta[property="og:site_name"]',    'property', 'og:site_name',        siteName);
    set('meta[property="og:title"]',        'property', 'og:title',            title);
    set('meta[property="og:description"]',  'property', 'og:description',      description);
    set('meta[property="og:url"]',          'property', 'og:url',              url);
    set('meta[property="og:image"]',        'property', 'og:image',            image);
    set('meta[property="og:image:secure_url"]', 'property', 'og:image:secure_url', image);
    set('meta[property="og:image:width"]',  'property', 'og:image:width',      '1200');
    set('meta[property="og:image:height"]', 'property', 'og:image:height',     '630');
    set('meta[name="twitter:card"]',         'name',     'twitter:card',        'summary_large_image');
    set('meta[name="twitter:title"]',        'name',     'twitter:title',       title);
    set('meta[name="twitter:description"]',  'name',     'twitter:description', description);
    set('meta[name="twitter:image"]',        'name',     'twitter:image',       image);

    let canonical = null;
    if (url) {
      const c = upsertLink('canonical', url);
      if (c) canonical = c;
    }

    // iter · Kill any stale/default image meta tags that don't match the
    // one we just set — e.g. the static `/og/og-default.jpg` FLYT-wings
    // card baked into index.html. When several og:image tags coexist,
    // link previewers (iMessage/WhatsApp) collage the story cover with
    // the brand default, producing the "half photo / half wings" preview.
    // Purging the mismatched tags leaves exactly the story cover.
    const purged = [];
    if (image) {
      const imgSel = 'meta[property="og:image"], meta[property="og:image:secure_url"], meta[name="twitter:image"]';
      document.querySelectorAll(imgSel).forEach((el) => {
        const c = el.getAttribute('content') || '';
        if (c && c !== String(image)) {
          purged.push({ el, parent: el.parentNode, next: el.nextSibling });
          el.remove();
        }
      });
    }

    return () => {
      document.title = prevTitle;
      restorers.forEach((fn) => { try { fn(); } catch { /* ignore */ } });
      purged.forEach(({ el, parent, next }) => {
        try { if (parent) parent.insertBefore(el, next); } catch { /* ignore */ }
      });
      if (canonical) {
        if (canonical.created) canonical.el.remove();
        else if (canonical.prev !== null) canonical.el.setAttribute('href', canonical.prev);
        else canonical.el.removeAttribute('href');
      }
    };
  }, [title, description, image, url, siteName, type]);
}

// Helper for callers that don't already know the touched meta names.
useDocumentHead.metaKeys = META_KEYS;
